import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus, X } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { ErrorState } from '../../../components/ui/ErrorState';
import { updateOperativeMails } from '../../../features/user/userThunks';
import {
  MAX_OPERATIVE_MAILS,
  isValidEmail,
} from '../../../features/user/userConstants';

const sameList = (a, b) => a.length === b.length && a.every((item, index) => item === b[index]);

/**
 * The addresses that get copies of the reports.
 *
 * **Nothing saves on its own.** Changes pile up locally and go out with the
 * button, because the endpoint replaces the whole array: an autosaving X would
 * make removing the wrong chip an instant, silent write. The card says when
 * there is something pending.
 *
 * @param {object} props
 * @param {string[]} props.emails - `operative_mails` from `get_user/`.
 */
export const ReportingEmailsCard = ({ emails }) => {
  const dispatch = useDispatch();
  const { isSaving } = useSelector((state) => state.user);

  const [draft, setDraft] = useState(emails || []);
  const [input, setInput] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState(null);

  // The saved list as last copied into the draft. The page fetches the user on
  // mount (twice under StrictMode), and a copy arriving while the user adds an
  // address used to wipe it. Only a list that really changed is taken, and
  // only while the draft has nothing pending.
  const synced = useRef(emails || []);

  useEffect(() => {
    const next = emails || [];
    const previous = synced.current;
    if (sameList(next, previous)) return;

    synced.current = next;
    setDraft((current) => (sameList(current, previous) ? next : current));
  }, [emails]);

  const isFull = draft.length >= MAX_OPERATIVE_MAILS;
  const isDirty =
    draft.length !== (emails || []).length ||
    draft.some((mail, index) => mail !== emails?.[index]);

  const handleAdd = (event) => {
    event.preventDefault();
    const value = input.trim().toLowerCase();

    if (!isValidEmail(value)) {
      setError('Escribe un correo válido');
      return;
    }
    if (draft.includes(value)) {
      setError('Ese correo ya está en la lista');
      return;
    }

    setDraft((prev) => [...prev, value]);
    setInput('');
    setAdding(false);
    setError(null);
  };

  const handleRemove = (mail) => {
    setDraft((prev) => prev.filter((item) => item !== mail));
    setError(null);
  };

  const handleSave = async () => {
    try {
      await dispatch(updateOperativeMails(draft)).unwrap();
      setError(null);
    } catch (err) {
      setError(typeof err === 'string' ? err : 'No se pudieron guardar los correos');
    }
  };

  const handleDiscard = () => {
    setDraft(emails || []);
    setInput('');
    setAdding(false);
    setError(null);
  };

  return (
    <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-8 shadow-sm border border-border">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h3 className="text-lg font-bold text-ninja-dark dark:text-white">
            Correos de Reportería
          </h3>
          <p className="text-sm text-text-secondary mt-1">
            Recibirán copias de los reportes y alertas. Hasta {MAX_OPERATIVE_MAILS}.
          </p>
        </div>
        <span className="text-sm font-semibold text-text-muted flex-shrink-0">
          {draft.length}/{MAX_OPERATIVE_MAILS}
        </span>
      </div>

      {error && (
        <div className="mb-4">
          <ErrorState variant="inline" title="Revisa esto" description={error} />
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        {draft.map((mail) => (
          <div
            key={mail}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-ninja-gray dark:bg-[#2d2d2d] border border-border text-sm font-medium text-text-secondary"
          >
            {mail}
            <button
              type="button"
              onClick={() => handleRemove(mail)}
              aria-label={`Quitar ${mail}`}
              className="text-text-muted hover:text-danger transition-colors"
            >
              <X size={14} />
            </button>
          </div>
        ))}

        {adding ? (
          <form onSubmit={handleAdd} className="flex items-center gap-2">
            <input
              type="email"
              autoFocus
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onBlur={() => !input && setAdding(false)}
              placeholder="correo@empresa.cl"
              aria-label="Nuevo correo de reportería"
              className="px-3 py-1.5 bg-white dark:bg-[#111] border border-border rounded-lg text-sm dark:text-white focus:outline-none focus:ring-2 focus:ring-ninja-green focus:border-transparent min-w-[220px]"
            />
            <Button variant="primary" size="sm" type="submit">
              Agregar
            </Button>
          </form>
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            disabled={isFull}
            title={isFull ? `El máximo es ${MAX_OPERATIVE_MAILS} correos` : undefined}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-ninja-greenLight border border-dashed border-ninja-green text-sm font-semibold text-ninja-green hover:bg-[rgba(0,214,147,0.15)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-ninja-greenLight"
          >
            <Plus size={14} /> Agregar correo
          </button>
        )}
      </div>

      {isFull && !adding && (
        <p className="text-xs text-text-muted mt-3">
          Llegaste al máximo de {MAX_OPERATIVE_MAILS} correos. Quita uno para agregar otro.
        </p>
      )}

      {isDirty && (
        <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-border">
          <Button variant="subtle" pill onClick={handleDiscard} disabled={isSaving}>
            Descartar
          </Button>
          <Button
            variant="primary"
            pill
            onClick={handleSave}
            loading={isSaving}
            loadingLabel="Guardando..."
          >
            Guardar cambios
          </Button>
        </div>
      )}
    </div>
  );
};
